import type { Editor } from '@tiptap/react';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import { useMemo } from 'react';

type TiptapEditorSnapshot = { editor: Editor | null };

function snapshotEquality(a: TiptapEditorSnapshot, b: TiptapEditorSnapshot | null): boolean {
    return a.editor === b?.editor;
}

/**
 * Hook that provides access to a Tiptap editor instance.
 *
 * Accepts an optional editor instance directly, or falls back to retrieving
 * the editor from the Tiptap context if available. This allows components
 * to work both when given an editor directly and when used within a Tiptap
 * editor context.
 *
 * @param providedEditor - Optional editor instance to use instead of the context editor
 * @returns The provided editor or the editor from context, whichever is available
 */
export function useTiptapEditor(providedEditor?: Editor | null): {
    editor: Editor | null;
} {
    const { editor: coreEditor } = useCurrentEditor();
    const mainEditor = useMemo(() => providedEditor || coreEditor, [providedEditor, coreEditor]);

    const snapshot = useEditorState({
        editor: mainEditor,
        selector(context): TiptapEditorSnapshot {
            return { editor: context.editor };
        },
        equalityFn: snapshotEquality,
    });

    return snapshot ?? { editor: null };
}
